import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  Users,
  MessageSquareText,
  CreditCard,
  User,
  BarChart2,
  PieChart as PieChartIcon,
  Calendar,
  CalendarDays,
  TrendingUp
} from 'lucide-react';
import { formatDateTime } from '../../../utils/helperFunction';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Dashboard component displaying organization statistics and recent employees
 * @param {Object} props - Component props
 * @param {Array} props.employees - List of employees
 * @param {Object} props.currentPlan - Current organization plan details
 * @param {Function} props.onViewAllEmployees - Handler for view all employees action
 * @returns {JSX.Element} Dashboard component
 */
const Dashboard = ({ onViewAllEmployees, messages = [] }) => {
  const { employees = [], currentPlan = {} } = useOutletContext();
  // State for chart view type
  const [chartView, setChartView] = useState('daily');
  
  // Calculate statistics
  const messageCount = messages?.length || 0;
  
  // Prepare data for charts
  const { messageData, employeeData, planUsageData } = useMemo(() => {
    // Group messages by time period
    const timeData = {};
    const employeeJoinData = {};
    const messageTypes = { sent: 0, received: 0 };
    
    // Process messages for charts
    messages.forEach(msg => {
      // Group by time period for message activity
      const date = new Date(msg.timestamp || msg.createdAt);
      let timeKey;
      
      if (chartView === 'monthly') {
        timeKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      } else {
        timeKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
      }
      
      timeData[timeKey] = (timeData[timeKey] || 0) + 1;
      
      // Count message types (simplified)
      if (msg.direction === 'outgoing') messageTypes.sent++;
      else messageTypes.received++;
    });
    
    // Process employee join dates
    if (employees && employees.length > 0) {
      employees.forEach(employee => {
        if (employee.createdAt) {
          const joinDate = new Date(employee.createdAt);
          const period = chartView === 'daily'
            ? joinDate.toLocaleDateString()
            : `${joinDate.getFullYear()}-${String(joinDate.getMonth() + 1).padStart(2, '0')}`;
          
          employeeJoinData[period] = (employeeJoinData[period] || 0) + 1;
        }
      });
    }
    
    // Sort time data
    const sortedTimeKeys = Object.keys(timeData).sort();
    const sortedEmployeeKeys = Object.keys(employeeJoinData).sort();
    
    const employeeLimit = currentPlan?.employeeLimit || 0;
    
    return {
      messageData: {
        labels: sortedTimeKeys,
        values: sortedTimeKeys.map(key => timeData[key])
      },
      employeeData: {
        labels: sortedEmployeeKeys,
        values: sortedEmployeeKeys.map(key => employeeJoinData[key])
      },
      planUsageData: {
        used: employees.length,
        remaining: employeeLimit === 'Unlimited' ? null : (employeeLimit - employees.length)
      },
      messageTypes: messageTypes
    };
  }, [employees, messages, currentPlan?.employeeLimit, chartView]);
  
  // Message types data is no longer used, keeping the destructure for future use
  // const { sent = 0, received = 0 } = msgTypes || {};
  
  // Chart options
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#9CA3AF'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFont: { size: 14 },
        bodyFont: { size: 12 },
        padding: 10,
        displayColors: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: '#9CA3AF'
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#9CA3AF'
        }
      }
    }
  };
  
  // Message activity chart data
  const messageChartData = {
    labels: messageData.labels,
    datasets: [
      {
        label: `Messages (${chartView === 'monthly' ? 'Monthly' : 'Daily'})`,
        data: messageData.values,
        backgroundColor: 'rgba(99, 102, 241, 0.2)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointBackgroundColor: 'rgba(99, 102, 241, 1)'
      },
    ],
  };
  
  // Employee growth chart data
  const employeeChartData = {
    labels: employeeData.labels,
    datasets: [
      {
        label: 'New Employees',
        data: employeeData.values,
        backgroundColor: 'rgba(16, 185, 129, 0.2)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 2,
        borderRadius: 4,
      },
    ],
  };
  
  const user = JSON.parse(localStorage.getItem('user')) || {};

  return (
    <div className="space-y-6 h-full overflow-y-auto custom-scrollbar">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Employees Card */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-fg-60">Total Employees</p>
              <h3 className="text-2xl font-semibold mt-1">{employees.length}</h3>
              <p className="text-xs text-fg-60 mt-1">
                {currentPlan?.employeeLimit === 'Unlimited' 
                  ? 'Unlimited employees allowed' 
                  : `${employees.length} of ${currentPlan?.employeeLimit || 0} used`}
              </p>
            </div>
            <div className="p-3 rounded-full bg-bg-50">
              <Users size={24} className="text-ac-02" />
            </div>
          </div>
        </div>

        {/* Messages This Month Card */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-fg-60">Messages This Month</p>
              <h3 className="text-2xl font-semibold mt-1">{messageCount}</h3>
              <p className="text-xs text-fg-60 mt-1">
                {currentPlan?.chatLimit === 'Unlimited' 
                  ? 'No message limit' 
                  : `Limit: ${currentPlan?.chatLimit || 0} messages`}
              </p>
            </div>
            <div className="p-3 rounded-full bg-bg-50">
              <MessageSquareText size={24} className="text-green-500" />
            </div>
          </div>
        </div>

        {/* Plan Usage Card */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-fg-60">Current Plan</p>
              <h3 className="text-2xl font-semibold mt-1">
                {user?.plan?.name ? user.plan.name.charAt(0).toUpperCase() + user.plan.name.slice(1) : 'N/A'}
              </h3>
              <p className="text-xs text-fg-60 mt-1">
                {user?.plan?.price === 0 ? 'Free plan' : user?.plan?.price ? `$${user.plan.price}/month` : 'N/A'}
              </p>
            </div>
            <div className="p-3 rounded-full bg-bg-50">
              <CreditCard size={24} className="text-purple-500" />
            </div>
          </div>
          <div className="pb-8">
          </div>
        </div>
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Message Activity Chart */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium flex items-center">
              <MessageSquareText size={20} className="mr-2 text-blue-500" />
              Message Activity
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setChartView('daily')}
                className={`px-2 py-1 text-xs rounded-md flex items-center ${
                  chartView === 'daily' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-bg-50 text-fg-60 hover:bg-bg-40'
                }`}
              >
                <Calendar size={14} className="mr-1" />
                Daily
              </button>
              <button
                onClick={() => setChartView('monthly')}
                className={`px-2 py-1 text-xs rounded-md flex items-center ${
                  chartView === 'monthly'
                    ? 'bg-blue-500 text-white' 
                    : 'bg-bg-50 text-fg-60 hover:bg-bg-40'
                }`}
              >
                <CalendarDays size={14} className="mr-1" />
                Monthly
              </button>
            </div>
          </div>
          <div className="h-64">
            <Line 
              data={messageChartData} 
              options={{
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  legend: { display: false }
                },
                maintainAspectRatio: false
              }} 
            />
          </div>
        </div>
        
        {/* Employee Growth Chart */}
        <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium flex items-center">
              <TrendingUp size={20} className="mr-2 text-green-500" />
              Employee Growth
            </h3>
          </div>
          <div className="h-64">
            <Bar 
              data={employeeChartData} 
              options={{
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  legend: { display: false }
                },
                maintainAspectRatio: false
              }} 
            />
          </div>
        </div>
      </div>

      {/* New Employees Section */}
      <div className="bg-bg-60 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">New Employees</h2>
          <button 
            onClick={onViewAllEmployees}
            className="text-xs text-ac-02 hover:text-ac-01"
          >
            View All
          </button>
        </div>
        {employees.length > 0 ? (
          <div className="space-y-4">
            {employees
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .slice(0, 5)
              .map((employee) => (
                <div key={employee._id} className="flex items-center pb-4 border-b border-bd-50 last:border-0 last:pb-0">
                  <div className="h-10 w-10 rounded-full bg-bg-50 flex items-center justify-center mr-3">
                    <User size={16} className="text-fg-60" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-fg-50">
                      {employee.name}
                    </p>
                    <p className="text-xs text-fg-60">
                      {employee.email}
                    </p>
                  </div>
                  <div className="text-xs text-fg-60">
                    {formatDateTime(employee.createdAt)}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-sm text-fg-60 text-center py-4">No employees found</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
